<template>
    <div class="tag-filter" ref="wrapper">
        <button
            type="button"
            class="trigger"
            :class="{ active: modelValue.length > 0, open: opened }"
            @click.stop.prevent="opened = !opened">
            <FeatherIcon name="tag" />
            <span>
                {{
                    modelValue.length === 0
                        ? 'Étiquettes'
                        : modelValue.length + ' étiquette' + (modelValue.length > 1 ? 's' : '')
                }}
            </span>
            <FeatherIcon name="chevron-down" />
        </button>

        <div v-if="opened" class="dropdown">
            <div class="dropdown-header">
                <span>Filtrer par étiquette</span>
                <button
                    v-if="modelValue.length"
                    type="button"
                    class="clear"
                    @click.stop.prevent="clear">
                    Effacer
                </button>
            </div>

            <div class="dropdown-body" v-if="tags.length">
                <label
                    v-for="tag in tags"
                    :key="tag.id"
                    class="row"
                    @click.stop>
                    <input
                        type="checkbox"
                        :checked="modelValue.includes(tag.id)"
                        @change="toggle(tag.id)" />
                    <TagChip :tag="tag" size="sm" />
                </label>
            </div>
            <div v-else class="empty">Aucune étiquette disponible</div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import type { Tag } from '@ezio/features/tags/types'
import FeatherIcon from '@ezio/ui/graphics/FeatherIcon.vue'
import TagChip from './TagChip.vue'

const props = withDefaults(
    defineProps<{
        tags: Tag[]
        modelValue: string[]
    }>(),
    {
        modelValue: () => []
    }
)

const emit = defineEmits<{ (e: 'update:modelValue', value: string[]): void }>()

const opened = ref(false)
const wrapper = ref<HTMLElement | null>(null)
onClickOutside(wrapper, () => (opened.value = false))

const toggle = (tagId: string) => {
    const set = new Set(props.modelValue)
    if (set.has(tagId)) set.delete(tagId)
    else set.add(tagId)
    emit('update:modelValue', Array.from(set))
}

const clear = () => {
    emit('update:modelValue', [])
}
</script>

<style lang="scss" scoped>
.tag-filter {
    position: relative;
    display: inline-block;
}

.trigger {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 8px;
    border: 1px solid var(--c-color-border-soft);
    background: #fff;
    color: var(--c-text-color);
    font-family: 'Sora', sans-serif;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.15s ease;

    :deep(.feather) {
        width: 14px;
        height: 14px;
    }

    &:hover {
        border-color: var(--c-color-text-soft);
    }

    &.active {
        border-color: var(--c-color-primary);
        color: var(--c-color-primary);
    }
}

.dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 6px;
    z-index: 50;
    background: #fff;
    border: 1px solid var(--c-color-border-soft);
    border-radius: 8px;
    min-width: 220px;
    max-width: 280px;
    box-shadow:
        0 1px 2px rgba(0, 0, 0, 0.04),
        0 4px 12px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid var(--c-color-border-soft);
    font-family: 'Sora', sans-serif;
    font-size: 0.75rem;
    color: var(--c-color-text-soft);

    .clear {
        background: transparent;
        border: 0;
        padding: 0;
        color: var(--c-color-primary);
        cursor: pointer;
        font-size: 0.75rem;
        font-family: inherit;
    }
}

.dropdown-body {
    max-height: 240px;
    overflow-y: auto;
    padding: 6px 0;
}

.row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    cursor: pointer;

    input[type='checkbox'] {
        margin: 0;
        cursor: pointer;
    }

    &:hover {
        background: rgba(32, 77, 79, 0.05);
    }
}

.empty {
    padding: 16px 12px;
    text-align: center;
    color: var(--c-color-text-soft);
    font-size: 0.8rem;
}
</style>
